import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { ReplaySubject, takeUntil } from 'rxjs';

import { MessagingService } from 'app/customer-api/services/messaging.service';
import { AppState } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';

import { CloseRoomNotification, LeaveRoomNotification, MessageType } from '../../../customer-api/protocol';
import {
  ModalSimpleSocialComponent,
  ModalSimpleSocialData,
} from '../modal-simple-social/modal-simple-social.component';
import { RoomPresenceService } from '../services/room/room-presence.service';
import { socialAppSelectors } from '../state/app';

const inIframe = () => {
  try {
    return window.self !== window.top;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) {
    return true;
  }
};

@Component({
  selector: 'app-social-leave-room',
  templateUrl: './social-leave-room.component.html',
  styleUrls: ['./social-leave-room.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SocialLeaveRoomComponent extends Loggable implements OnDestroy {
  @Input() private inSocial = false;
  public inIframe = signal(false);
  private isHost = signal(false);
  private customerAPIEnabled = false;
  private destroyed$ = new ReplaySubject<boolean>(1);
  public isPresentationActive = signal(false);

  public constructor(
    private dialog: MatDialog,
    private roomPresenceService: RoomPresenceService,
    private store: Store<AppState>,
    private messagingService: MessagingService,
  ) {
    super();
    // check to see if user is using customer api
    this.store
      .select(socialAppSelectors.customerAPIEnabled)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((api) => {
        this.customerAPIEnabled = api;
        if (!this.customerAPIEnabled) {
          this.inIframe.set(inIframe());
        }
      });

    this.store
      .select(socialAppSelectors.isHost)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isHost: boolean) => {
        this.isHost.set(isHost);
      });

    this.store
      .select(socialAppSelectors.isPresentationActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPresentationActive) => {
        this.isPresentationActive.set(isPresentationActive);
      });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.dialog.closeAll();
  }

  public leave(): void {
    const data =
      this.isHost() && this.inSocial
        ? {
            modalTitle: 'Leave Room',
            text: 'Close Room For All to close the room for everyone, or Leave Room to keep the room open.',
            primaryButtonText: 'Leave Room',
            secondaryButtonText: 'Close Room For All',
            tertiaryButtonText: 'Cancel',
          }
        : {
            modalTitle: 'Leave Room',
            text: 'Are you sure you want to leave this room?',
            primaryButtonText: 'Leave Room',
            tertiaryButtonText: 'Cancel',
          };

    const dialogRef = this.dialog.open<ModalSimpleSocialComponent, ModalSimpleSocialData>(ModalSimpleSocialComponent, {
      data,
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result) => {
        if (result === 'primary') {
          this.closePiP();
          this.handleGuestLeaveRoom();
          this.roomPresenceService.leaveRoom();

          // End Social for All
        } else if (result === 'secondary' && this.isHost()) {
          this.closePiP();
          this.handleGuestCloseRoom();
          this.roomPresenceService.closeRoom();
        }
      });
  }

  private handleGuestLeaveRoom(): void {
    if (this.customerAPIEnabled) {
      const notification: LeaveRoomNotification = {
        type: MessageType.LEAVE_ROOM_NOTIFICATION,
        context: 'preciate',
      };
      this.messagingService.postNotification(notification);
    }
  }

  private handleGuestCloseRoom(): void {
    if (this.customerAPIEnabled) {
      const notification: CloseRoomNotification = {
        type: MessageType.CLOSE_ROOM_NOTIFICATION,
        context: 'preciate',
      };
      this.messagingService.postNotification(notification);
    }
  }

  private closePiP() {
    if (document.pictureInPictureElement) {
      document
        .exitPictureInPicture()
        .then(() => this.info('Document Exited from Picture-in-Picture mode'))
        .catch((err) => this.error(err));
    }
  }
}
